import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { TemplateDraft as Entity, FinalizeTemplateDraftResult } from './template-draft.model';
import { Template } from '../template/template.model';
import { TemplateDraftActions as EntityActions } from './template-draft.actions';
import { TemplateDraftService } from './template-draft.service';

@Injectable()
export class TemplateDraftEffects {
    loadSingle$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EntityActions.loadSingle),
            switchMap(({ draftId, correlationId }) =>
                this.service.get(draftId).pipe(
                    map((entity: Entity) =>
                        EntityActions.loadSingleSuccess({ entity, correlationId })
                    ),
                    catchError((error) =>
                        of(EntityActions.loadSingleFailure({ error, correlationId }))
                    )
                )
            )
        )
    );

    loadByCategory$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EntityActions.loadByCategory),
            switchMap(({ categoryId, status, correlationId }) =>
                this.service.getByCategory(categoryId, status).pipe(
                    map((entities: Entity[]) =>
                        EntityActions.loadByCategorySuccess({ entities, correlationId })
                    ),
                    catchError((error) =>
                        of(EntityActions.loadByCategoryFailure({ error, correlationId }))
                    )
                )
            )
        )
    );

    create$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EntityActions.create),
            switchMap(({ entity, correlationId }) =>
                this.service.create(entity).pipe(
                    map((result: Entity) =>
                        EntityActions.createSuccess({ entity: result, correlationId })
                    ),
                    catchError((error) =>
                        of(EntityActions.createFailure({ error, correlationId }))
                    )
                )
            )
        )
    );

    update$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EntityActions.update),
            switchMap(({ entity, correlationId }) =>
                this.service.update(entity).pipe(
                    map((result: Entity) =>
                        EntityActions.updateSuccess({ entity: result, correlationId })
                    ),
                    catchError((error) =>
                        of(EntityActions.updateFailure({ error, correlationId }))
                    )
                )
            )
        )
    );

    remove$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EntityActions.remove),
            switchMap(({ draftId, correlationId }) =>
                this.service.remove(draftId).pipe(
                    map(() =>
                        EntityActions.removeSuccess({ draftId, correlationId })
                    ),
                    catchError((error) =>
                        of(EntityActions.removeFailure({ error, correlationId }))
                    )
                )
            )
        )
    );

    addTemplates$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EntityActions.addTemplates),
            switchMap(({ draftId, productIds, correlationId }) =>
                this.service.addTemplates(draftId, productIds).pipe(
                    map((templates: Template[]) =>
                        EntityActions.addTemplatesSuccess({ templates, correlationId })
                    ),
                    catchError((error) =>
                        of(EntityActions.addTemplatesFailure({ error, correlationId }))
                    )
                )
            )
        )
    );

    finalize$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EntityActions.finalize),
            switchMap(({ draftId, request, correlationId }) =>
                this.service.finalize(draftId, request).pipe(
                    map((result: FinalizeTemplateDraftResult) =>
                        EntityActions.finalizeSuccess({ result, draftId, correlationId })
                    ),
                    catchError((error) =>
                        of(EntityActions.finalizeFailure({ error, correlationId }))
                    )
                )
            )
        )
    );

    startEdit$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EntityActions.startEdit),
            switchMap(({ draftId, correlationId }) =>
                this.service.startEdit(draftId).pipe(
                    map((entity: Entity) =>
                        EntityActions.startEditSuccess({ entity, correlationId })
                    ),
                    catchError((error) =>
                        of(EntityActions.startEditFailure({ error, correlationId }))
                    )
                )
            )
        )
    );

    editTemplate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EntityActions.editTemplate),
            switchMap(({ templateId, correlationId }) =>
                this.service.editTemplate(templateId).pipe(
                    map((entity: Entity) =>
                        EntityActions.editTemplateSuccess({ entity, correlationId })
                    ),
                    catchError((error) =>
                        of(EntityActions.editTemplateFailure({ error, correlationId }))
                    )
                )
            )
        )
    );

    editTemplates$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EntityActions.editTemplates),
            switchMap(({ templateIds, correlationId }) =>
                this.service.editTemplates(templateIds).pipe(
                    map((entity: Entity) =>
                        EntityActions.editTemplatesSuccess({ entity, correlationId })
                    ),
                    catchError((error) =>
                        of(EntityActions.editTemplatesFailure({ error, correlationId }))
                    )
                )
            )
        )
    );

    createOrder$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EntityActions.createOrder),
            switchMap(({ templateIds, bundleIds, categoryIds, correlationId }) =>
                this.service.createOrder({ templateIds, bundleIds, categoryIds }).pipe(
                    map((entity: Entity) =>
                        EntityActions.createOrderSuccess({ entity, correlationId })
                    ),
                    catchError((error) =>
                        of(EntityActions.createOrderFailure({ error, correlationId }))
                    )
                )
            )
        )
    );

    editSelection$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EntityActions.editSelection),
            switchMap(({ templateIds, bundleIds, categoryIds, correlationId }) =>
                this.service.editSelection({ templateIds, bundleIds, categoryIds }).pipe(
                    map((entity: Entity) =>
                        EntityActions.editSelectionSuccess({ entity, correlationId })
                    ),
                    catchError((error) =>
                        of(EntityActions.editSelectionFailure({ error, correlationId }))
                    )
                )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private service: TemplateDraftService
    ) {}
}
